import React, { useEffect, useMemo, useState } from "react";
import { Box, Typography, Chip, IconButton, Tooltip, Avatar, Switch } from "@mui/material";
import { MaterialReactTable } from "material-react-table";
import { useDispatch, useSelector } from "react-redux";
import AddIcon from "@mui/icons-material/Add";
import SettingsIcon from "@mui/icons-material/Settings";
import SyncIcon from "@mui/icons-material/Sync";
import DeleteIcon from "@mui/icons-material/Delete";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { setModal } from "../../features/modal/ModalSlice";
import CustomButton from "../../components/Button/CustomButton";

const Integrations = () => {
  const dispatch = useDispatch();
  const modal = useSelector((state) => state.modal?.modal);

  const [integrations, setIntegrations] = useState([
    {
      id: 1,
      name: "Salesforce",
      category: "CRM",
      description: "Sync contacts, leads, and opportunities",
      status: "Connected",
      enabled: true,
      lastSync: "2024-01-15 11:30 AM",
      recordsSynced: 2456,
      logo: "https://cdn.worldvectorlogo.com/logos/salesforce-2.svg",
    },
    {
      id: 2,
      name: "Google Calendar",
      category: "Calendar",
      description: "Schedule and manage appointments",
      status: "Connected",
      enabled: true,
      lastSync: "2024-01-15 10:45 AM",
      recordsSynced: 1234,
      logo: "https://cdn.worldvectorlogo.com/logos/google-calendar.svg",
    },
    {
      id: 3,
      name: "HubSpot",
      category: "CRM",
      description: "Manage contacts and track deals",
      status: "Connected",
      enabled: true,
      lastSync: "2024-01-15 09:20 AM",
      recordsSynced: 3421,
      logo: "https://cdn.worldvectorlogo.com/logos/hubspot-1.svg",
    },
    {
      id: 4,
      name: "Slack",
      category: "Communication",
      description: "Send notifications and alerts",
      status: "Connected",
      enabled: false,
      lastSync: "2024-01-14 05:30 PM",
      recordsSynced: 89,
      logo: "https://cdn.worldvectorlogo.com/logos/slack-new-logo.svg",
    },
    {
      id: 5,
      name: "Stripe",
      category: "Payment",
      description: "Process payments and subscriptions",
      status: "Connected",
      enabled: true,
      lastSync: "2024-01-15 11:15 AM",
      recordsSynced: 567,
      logo: "https://cdn.worldvectorlogo.com/logos/stripe-4.svg",
    },
    {
      id: 6,
      name: "Zendesk",
      category: "Support",
      description: "Create and track support tickets",
      status: "Error",
      enabled: true,
      lastSync: "2024-01-12 02:30 PM",
      recordsSynced: 0,
      logo: "https://cdn.worldvectorlogo.com/logos/zendesk-1.svg",
    },
  ]);

  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (modal?.isUpdatedIn === "INTEGRATIONS") {
      fetchIntegrations();
    }
  }, [modal]);

  const fetchIntegrations = async () => {
    setLoading(true);
    try {
      setTimeout(() => setLoading(false), 500);
    } catch (error) {
      console.error("Error fetching integrations:", error);
      setLoading(false);
    }
  };

  const handleAddIntegration = () => {
    dispatch(setModal({ type: "ADD_INTEGRATION" }));
  };

  const handleConfigureIntegration = (integration) => {
    dispatch(setModal({ type: "CONFIGURE_INTEGRATION", data: integration }));
  };

  const handleSyncIntegration = (integration) => {
    alert(`Syncing ${integration.name}...`);
  };

  const handleDeleteIntegration = (integration) => {
    if (window.confirm(`Disconnect ${integration.name}?`)) {
      setIntegrations(integrations.filter((i) => i.id !== integration.id));
    }
  };

  const handleToggleEnabled = (integration) => {
    setIntegrations(
      integrations.map((i) =>
        i.id === integration.id ? { ...i, enabled: !i.enabled } : i
      )
    );
  };

  const getCategoryColor = (category) => {
    const colors = {
      CRM: { bg: "#E3F2FD", color: "#1565C0" },
      Calendar: { bg: "#E8F5E9", color: "#2E7D32" },
      Communication: { bg: "#F3E5F5", color: "#7B1FA2" },
      Payment: { bg: "#FFF3E0", color: "#E65100" },
      Support: { bg: "#FFF9C4", color: "#F57F17" },
    };
    return colors[category] || { bg: "#F5F5F5", color: "#666" };
  };

  const getStatusColor = (status) => {
    const colors = {
      Connected: "success",
      Error: "error",
      Disconnected: "default",
    };
    return colors[status] || "default";
  };

  const columns = useMemo(
    () => [
      {
        accessorKey: "name",
        header: "Integration",
        size: 250,
        Cell: ({ row }) => (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <Avatar
              src={row.original.logo}
              alt={row.original.name}
              sx={{ width: 40, height: 40 }}
            />
            <Box>
              <Typography variant="body2" fontWeight={600}>
                {row.original.name}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {row.original.description}
              </Typography>
            </Box>
          </Box>
        ),
      },
      {
        accessorKey: "category",
        header: "Category",
        size: 120,
        Cell: ({ cell }) => {
          const colors = getCategoryColor(cell.getValue());
          return (
            <Chip
              label={cell.getValue()}
              size="small"
              sx={{
                backgroundColor: colors.bg,
                color: colors.color,
                fontWeight: 600,
              }}
            />
          );
        },
      },
      {
        accessorKey: "status",
        header: "Status",
        size: 120,
        Cell: ({ cell }) => (
          <Chip
            label={cell.getValue()}
            size="small"
            color={getStatusColor(cell.getValue())}
            icon={
              cell.getValue() === "Connected" ? (
                <CheckCircleIcon sx={{ fontSize: 16 }} />
              ) : undefined
            }
            sx={{ fontWeight: 500 }}
          />
        ),
      },
      {
        accessorKey: "enabled",
        header: "Enabled",
        size: 100,
        Cell: ({ row }) => (
          <Switch
            checked={row.original.enabled}
            onChange={() => handleToggleEnabled(row.original)}
            size="small"
          />
        ),
      },
      {
        accessorKey: "lastSync",
        header: "Last Sync",
        size: 150,
        Cell: ({ cell }) => (
          <Typography variant="body2" color="text.secondary" sx={{ fontSize: 12 }}>
            {cell.getValue()}
          </Typography>
        ),
      },
      {
        accessorKey: "recordsSynced",
        header: "Records Synced",
        size: 130,
        Cell: ({ cell }) => (
          <Typography variant="body2" fontWeight={600} color="primary">
            {cell.getValue().toLocaleString()}
          </Typography>
        ),
      },
    ],
    []
  );

  const totalIntegrations = integrations.length;
  const connectedIntegrations = integrations.filter((i) => i.status === "Connected").length;
  const enabledIntegrations = integrations.filter((i) => i.enabled).length;
  const totalRecordsSynced = integrations.reduce((sum, i) => sum + i.recordsSynced, 0);

  return (
    <Box sx={{ padding: 3 }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", mb: 3 }}>
        <Box>
          <Typography variant="h4" gutterBottom fontWeight={600}>
            Integrations
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Connect your favorite tools and services
          </Typography>
        </Box>
        <CustomButton
          icon={<AddIcon />}
          iconPosition="left"
          onClick={handleAddIntegration}
          text="Add Integration"
        />
      </Box>

      <Box sx={{ display: "flex", gap: 2, mb: 3 }}>
        <Box sx={{ flex: 1, p: 2, borderRadius: 2, backgroundColor: "#F3F4F6" }}>
          <Typography variant="h4" fontWeight={700} color="primary">
            {totalIntegrations}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Total Integrations
          </Typography>
        </Box>
        <Box sx={{ flex: 1, p: 2, borderRadius: 2, backgroundColor: "#F3F4F6" }}>
          <Typography variant="h4" fontWeight={700} color="success.main">
            {connectedIntegrations}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Connected
          </Typography>
        </Box>
        <Box sx={{ flex: 1, p: 2, borderRadius: 2, backgroundColor: "#F3F4F6" }}>
          <Typography variant="h4" fontWeight={700} color="info.main">
            {enabledIntegrations}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Active
          </Typography>
        </Box>
        <Box sx={{ flex: 1, p: 2, borderRadius: 2, backgroundColor: "#F3F4F6" }}>
          <Typography variant="h4" fontWeight={700} color="warning.main">
            {totalRecordsSynced.toLocaleString()}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Records Synced
          </Typography>
        </Box>
      </Box>

      <MaterialReactTable
        columns={columns}
        data={integrations}
        state={{ isLoading: loading }}
        enableRowActions
        positionActionsColumn="last"
        renderRowActions={({ row }) => (
          <Box sx={{ display: "flex", gap: 0.5 }}>
            <Tooltip title="Sync Now">
              <IconButton
                size="small"
                onClick={() => handleSyncIntegration(row.original)}
                sx={{ color: "info.main" }}
                disabled={row.original.status !== "Connected"}
              >
                <SyncIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title="Configure">
              <IconButton
                size="small"
                onClick={() => handleConfigureIntegration(row.original)}
                sx={{ color: "primary.main" }}
              >
                <SettingsIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title="Disconnect">
              <IconButton
                size="small"
                onClick={() => handleDeleteIntegration(row.original)}
                sx={{ color: "error.main" }}
              >
                <DeleteIcon />
              </IconButton>
            </Tooltip>
          </Box>
        )}
        enableColumnFilters
        enableGlobalFilter
        enableSorting
        enablePagination
        initialState={{
          pagination: { pageSize: 10, pageIndex: 0 },
        }}
        muiTablePaperProps={{
          elevation: 0,
          sx: {
            borderRadius: 2,
            border: "1px solid #E0E0E0",
          },
        }}
        muiTableHeadCellProps={{
          sx: {
            backgroundColor: "#F9FAFB",
            fontWeight: 600,
          },
        }}
      />
    </Box>
  );
};

export default Integrations;
